import React, { createContext, ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { ActivityIndicator, Modal, Text, View } from 'react-native'
import { ChatEvent, CustomerData, Token, WidgetState } from '@livechat/widget-core'
import { LiveChatWidget, useWidgetCustomerData } from '@livechat/widget-react'
import { useTranslation } from 'react-i18next'

import { BASE_URL, LIVECHAT_LICENSE, PUBLIC_ENV } from '@/constants/config'
import { AuthContext, setSignOutCallback } from '@/stores/auth'
import { Button } from '@/components/base'

// Create a context for the notifications
export const CustomerServiceContext = createContext<{
  openChat: () => void
  numUnread: number
}>({
  openChat: () => {},
  numUnread: 0
})

interface providerProps {
  children: ReactNode
}

const defaultSessionVariables: CustomerData['sessionVariables'] = {
  customerType: 'User'
}

export function CustomerServiceProvider({ children }: providerProps) {
  const { t } = useTranslation()
  const customerData = useWidgetCustomerData()
  const [visibility, setVisibility] = useState<WidgetState['visibility']>('hidden')
  const cachedToken = useRef<Token | null>(null)
  const sessionVariables = useRef<CustomerData['sessionVariables']>(defaultSessionVariables)
  const [numUnread, setNumUnread] = useState(0)
  const [initialized, setInitialized] = useState(false)
  const [failed, setFailed] = useState(false)
  const [attempt, setAttempt] = useState(0)

  const { id, user, loggedIn, authToken, jwt } = AuthContext.useSelector((state) => {
    return {
      id: state.context.values?.user.id,
      user: state.context.values?.user,
      loggedIn: state.matches('LOGGED_IN'),
      authToken: state.context.values?.lcToken,
      jwt: state.context.values?.jwt
    }
  })

  // Clear any cached LiveChat state on logout
  useEffect(() => {
    setSignOutCallback(() => {
      cachedToken.current = null
      sessionVariables.current = { ...defaultSessionVariables }
      setVisibility('hidden')
      setNumUnread(0)
    })
  }, [])

  useEffect(() => {
    if (authToken) {
      let token: Token | null = null
      try {
        const parsed = JSON.parse(authToken) as Token | null
        token = parsed?.accessToken && parsed.entityId ? parsed : null
      } catch (error) {
        console.warn('Ignoring an invalid LiveChat token', error)
      }
      cachedToken.current = token
      if (token) {
        sessionVariables.current.userChatId = token.entityId
        sessionVariables.current.uid = user?.id ?? ''
      }

      if (PUBLIC_ENV !== 'prod') {
        sessionVariables.current.testEnvironment = PUBLIC_ENV ?? 'true'
      }
    } else {
      cachedToken.current = null
    }
    // Missing tokens are recovered on demand before mounting the widget.
    setInitialized(Boolean(id && jwt && cachedToken.current))
  }, [authToken, id, jwt])

  const openChat = () => {
    setFailed(false)
    setAttempt((value) => value + 1)
    setVisibility('maximized')
  }

  const fetchNewToken = useCallback(
    async (signal?: AbortSignal): Promise<Token> => {
      const fallbackToken = cachedToken.current

      try {
        // Avoid hitting secure endpoint when we have no auth context yet
        if (!loggedIn || !jwt) {
          throw new Error('not authenticated')
        }

        if (loggedIn) {
          const resp = await fetch(`${BASE_URL}/mobile/secure/auth/glct`, {
            method: 'POST',
            signal: signal ?? AbortSignal.timeout(15000),
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${jwt}`
            }
          })
          if (!resp.ok) {
            const errResp = await resp.json().catch(() => null)
            throw new Error(errResp?.error ?? `LiveChat token refresh failed: ${resp.status}`)
          }

          const token = (await resp.json()) as Token
          signal?.throwIfAborted()
          if (!token?.accessToken || !token.entityId) throw new Error('invalid livechat token')
          cachedToken.current = token
          sessionVariables.current.userChatId = cachedToken.current.entityId
        }

        sessionVariables.current.uid = user?.id ?? ''
        if (PUBLIC_ENV !== 'prod') {
          sessionVariables.current.testEnvironment = PUBLIC_ENV ?? 'true'
        }

        if (!cachedToken.current) {
          throw new Error('missing livechat token')
        }
        return cachedToken.current
      } catch (e) {
        if (signal?.aborted) throw e
        // A cached token is still usable when the refresh endpoint is temporarily
        // unavailable. Returning it keeps an optional support widget from taking
        // down the rest of the application.
        if (cachedToken.current || fallbackToken) {
          const message = e instanceof Error ? e.message : String(e)
          console.warn(`LiveChat token refresh failed; using cached token: ${message}`)
          return cachedToken.current ?? fallbackToken!
        }
        throw e
      }
    },
    [loggedIn, jwt, user?.id]
  )

  // Bootstrap outside the widget: it cannot request its own missing token while
  // unmounted. Keep failures in our UI so the customer can explicitly retry.
  useEffect(() => {
    if (!loggedIn || !jwt || initialized || visibility !== 'maximized') return
    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), 15000)
    let active = true
    setFailed(false)
    fetchNewToken(controller.signal)
      .then(() => {
        if (active) setInitialized(true)
      })
      .catch(() => {
        if (active) setFailed(true)
      })
      .finally(() => clearTimeout(timeout))
    return () => {
      active = false
      clearTimeout(timeout)
      controller.abort()
    }
  }, [loggedIn, jwt, initialized, visibility, attempt, fetchNewToken])

  const LiveChatIdentityProvider = () => {
    return {
      getToken: async () => {
        if (cachedToken.current) {
          return cachedToken.current
        }
        return fetchNewToken()
      },
      getFreshToken: fetchNewToken,
      hasToken: () => Promise.resolve(Boolean(cachedToken.current || (loggedIn && jwt && authToken))),
      invalidate: () => {
        return Promise.resolve()
      }
    }
  }

  function handleEvent(event: ChatEvent): void {
    // TODO: Handle case where the user received offline messages
    if (!customerData?.isReturning) return

    if (event.author.type === 'agent') {
      setNumUnread((prev) => prev + 1)
    }
  }

  const name = user?.lastName ? `${user?.lastName} ${user?.firstName}` : `UID${user?.id}`

  return (
    <CustomerServiceContext.Provider value={{ openChat, numUnread }}>
      {loggedIn && initialized && (
        <LiveChatWidget
          customerName={name}
          onNewEvent={handleEvent}
          onVisibilityChanged={(event) => {
            switch (event.visibility) {
              case 'hidden':
              case 'minimized':
                setVisibility('hidden')
                break
              case 'maximized':
                setNumUnread(0)
            }
          }}
          license={LIVECHAT_LICENSE}
          visibility={visibility}
          customIdentityProvider={LiveChatIdentityProvider}
          sessionVariables={sessionVariables.current}
        />
      )}
      {children}
      <Modal
        transparent
        visible={loggedIn && !initialized && visibility === 'maximized'}
        onRequestClose={() => setVisibility('hidden')}
      >
        <View className={'flex-1 items-center justify-center bg-black/30 p-4'}>
          <View className={'w-full max-w-sm gap-4 rounded-xl bg-background-primary p-4'}>
            <Text className={'text-base font-medium text-text-primary'}>{t('support:contactCustomerService')}</Text>
            {failed ? (
              <Text className={'text-sm text-text-secondary'}>{t('support:temporarilyUnavailable')}</Text>
            ) : (
              <ActivityIndicator />
            )}
            {failed && <Button onPress={openChat}>{t('support:retry')}</Button>}
            <Button variant={'outline'} onPress={() => setVisibility('hidden')}>
              {t('support:close')}
            </Button>
          </View>
        </View>
      </Modal>
    </CustomerServiceContext.Provider>
  )
}

export default CustomerServiceProvider

// // Subscribe to AuthContext
// const MockLiveChatIdentityProvider = () => {
//   return {
//     // Get cached token from authState
//     getToken: async () => {
//       console.log("getToken", cachedToken.current)
//       return {
//         accessToken: "dal:53EX0A4AToeVKdhFvkNpFA",
//         entityId: "f08a935b-f66b-4f01-91ac-fe00d9c1ca2a",
//         expiresIn: 14400,
//         tokenType: 'Bearer',
//         creationDate: Date.now(),
//         licenseId: 17672508
//       } as Token
//     },
//     // Get cached token from authState
//     getFreshToken: async () => {
//       console.log("getFreshToken")
//       return {
//         accessToken: "dal:8j5CY9yHTZ6fL5n7PjIy7Q",
//         entityId: "b5b68f77-da57-44fc-bbc3-afa4d8ef20a0",
//         expiresIn: 14400,
//         tokenType: "Bearer",
//         creationDate: Date.parse("2024-06-02T08:45:53.373418Z"),
//         licenseId: 17672508
//       } as Token
//     },
//     hasToken: () => Promise.resolve(true),
//     invalidate: () => { return Promise.resolve() },
//   }
// }
